// Copyright (c) StrangeBee 2023

import THTag from '@Atoms/THTag/THTag';
import { ICONS } from '@Constants/iconsApp';
import { classList } from '@Helpers/className';
import { UseLocalMessageType } from '@Helpers/locale';
import { THCheckbox } from '@Molecules/Inputs/THCheckbox/THCheckbox';
import { THInputNumber } from '@Molecules/Inputs/THInputNumber/THInputNumber';
import { THRangePicker } from '@Molecules/Inputs/THRangePicker/THRangePicker';
import { THTagSelector } from '@Molecules/Inputs/THTagSelector/THTagSelector';
import { THUserSelector } from '@Molecules/Inputs/THUserSelector/THUserSelector';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { Space } from 'antd';
import moment from 'moment';
import React, { ReactElement } from 'react';
import { DescribeFieldAttribute, SearchFormInterface, UserOutput } from 'thehive-sdk';

type InputItemProps = {
    value: any;
    onChange: (newValue: any, params?: SearchFormInterface['params']) => any;
    valueList?: any[];
    labelList?: any[];
    className?: string;
    disabled?: boolean;
    i: UseLocalMessageType;
    attribute: DescribeFieldAttribute;
    filter?: Partial<SearchFormInterface>;
    userList: UserOutput[];
};

const date = ({ value, onChange, className, disabled }: InputItemProps): ReactElement => (
    <THRangePicker
        value={value?.map((timestamp: number) => moment(timestamp))}
        onChange={(values): void => {
            const timestamps = values?.map((date) => date?.valueOf());
            onChange(timestamps);
        }}
        className={classList(className)}
        disabled={disabled}
        showTime
    />
);

const number = ({ value, onChange, className, disabled, i }: InputItemProps): ReactElement => (
    <THInputNumber
        placeholder={i('filter.form.number.placeholder')}
        onChange={(value): void => onChange(value)}
        value={value}
        className={className}
        disabled={disabled}
    />
);

const enumeration = ({
    value,
    onChange,
    className,
    disabled,
    valueList,
    labelList,
    i,
    attribute,
    filter,
}: InputItemProps): ReactElement => {
    const options = (valueList || []).map((value, index) => ({
        label: labelList?.[index] || value,
        value,
    }));

    return (
        <THTagSelector
            taxonomies={false}
            placeholder={i('filter.form.values.placeholder')}
            options={options}
            value={value}
            onChange={(event) => onChange(event)}
            className={className}
            disabled={disabled}
            tagRender={({ label, onClose }): ReactElement => (
                <THTag value={label as string} onClick={() => onClose()} full textBlack />
            )}
            suffix={
                ['fulltext', 'fulltextOnly'].includes(attribute?.indexType) && (
                    <Space align="center">
                        <THCheckbox
                            checked={filter?.params?.strict}
                            onChange={(e) => onChange(value, { strict: e.target.checked })}
                        />
                        <ICONS.MatchWord />
                    </Space>
                )
            }
            optionFilterProp="label"
            filterOption={false}
        />
    );
};

const boolean = () => null;

const tags = ({ value, onChange, className, i, attribute, filter }: InputItemProps): ReactElement => (
    <THTagSelector.Autocomplete
        taxonomies={attribute?.name === 'tags'}
        placeholder={i('filter.form.values.placeholder')}
        value={value}
        onChange={(value) => onChange(value)}
        className={className}
        suffix={
            ['fulltext', 'fulltextOnly'].includes(attribute?.indexType) && (
                <Space align="center">
                    <THCheckbox
                        checked={filter?.params?.strict}
                        onChange={(e) => onChange(value, { strict: e.target.checked })}
                    />
                    <ICONS.MatchWord />
                </Space>
            )
        }
    />
);

const string = ({ value, onChange, className, disabled, i, attribute, filter }: InputItemProps): ReactElement => (
    <THTagSelector.Autocomplete
        taxonomies={false}
        placeholder={i('filter.form.values.placeholder')}
        value={value}
        onChange={(event): void => onChange(event)}
        tagRender={({ label, onClose }): ReactElement => (
            <THTag value={label as string} onClick={() => onClose()} full textBlack />
        )}
        suffix={
            ['fulltext', 'fulltextOnly'].includes(attribute?.indexType) && (
                <Space align="center">
                    <THCheckbox
                        checked={filter?.params?.strict}
                        onChange={(e) => onChange(value, { strict: e.target.checked })}
                    />
                    <THTooltip title={i('general.strict-mode')}>
                        <ICONS.MatchWord />
                    </THTooltip>
                </Space>
            )
        }
        className={className}
        disabled={disabled}
    />
);

const url = ({ value, onChange, className, disabled, i }: InputItemProps): ReactElement => (
    <THTagSelector.Autocomplete
        taxonomies={false}
        placeholder={i('filter.form.values.placeholder')}
        value={value}
        onChange={(event): void => onChange(event)}
        tagRender={({ label, onClose }): ReactElement => (
            <THTag value={label as string} onClick={() => onClose()} full textBlack />
        )}
        className={className}
        disabled={disabled}
    />
);
const user = ({ value, onChange, className, disabled, i, userList }: InputItemProps): ReactElement => {
    return (
        <THUserSelector
            users={userList}
            value={value}
            onChange={(value) => onChange(value)}
            className={className}
            disabled={disabled}
            placeholder={i('filter.form.values.placeholder')}
            mode="multiple"
            borderless={false}
        />
    );
};

export default {
    date,
    number,
    boolean,
    enumeration,
    string,
    url,
    tags,
    user,
} as Record<string, (...args: any[]) => any>;
